import React, { useState } from "react";
import styled from "styled-components";
import { Button, Card, Title, Modal, Group } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { shallow } from "zustand/shallow";
import axios from "axios";
import { getUuid } from "@util/getUuid";
import { useTranslation } from "react-i18next";

import FieldLibrary from "./FieldLibrary";
import FieldItem from "./EffortFieldItem";
import FieldForm from "./EffortFieldForm";
import { EffortSavedForms } from "@components/Effort";

import { addressKeys } from "./FieldLibrary";
import useAppStore from "./effort-builder-store";

export default function EffortFields({
  effortId,
  onChange,
  postmarkTemplateNames,
  eiWithTriggerId,
  entityName,
  effortName,
}) {
  const { t } = useTranslation();
  const [customVisible, setCustomVisble] = useState(false);
  const [libraryVisible, setLibraryVisible] = useState(false);
  const { fields, languages } = useAppStore(
    (state) => ({
      fields: state.effortState.fields,
      languages: state.effortState.languages,
    }),
    shallow
  );

  function onFieldUpdate(updatedField) {
    if (updatedField.options.length) {
      const options = updatedField.options.map((m) => ({
        text: m.text,
        effort_id: effortId,
      }));
    }

    onChange(
      [...fields].map((x) => {
        const item = fields.find((f) => x.uuid === updatedField.uuid);
        return item ? updatedField : x;
      })
    );
  }

  function onFieldRemove(uuid) {
    const fieldInfo = fields.find((f) => f.uuid === uuid);
    if (!fieldInfo) return;

    if (fieldInfo.name === "phone") {
      return onChange(fields.filter((f) => f.uuid !== uuid));
    } else {
      if (addressKeys.includes(fieldInfo.name)) {
        onChange(fields.filter((f) => !addressKeys.includes(f.name)));
      } else {
        onChange(fields.filter((f) => f.name !== fieldInfo.name));
      }
      // onChange(
      //   fields.filter((f) =>
      //     f.db_property && addressKeys.includes(f.db_property)
      //       ? !addressKeys.includes(f.db_property)
      //       : f.uuid !== uuid
      //   )
      // );
    }
    // onChange(fields.filter((f) => f.uuid !== uuid));
  }

  function onFieldLibraryAdd(e) {
    if (e.dynamic_label_field === "sms-opt-in") {
      return createEngagementItem(e);
    } else {
      onChange([...fields, { ...e, editable: false }]);
    }
    setLibraryVisible(false);
  }

  function createEngagementItem(e) {
    const req = {
      ...e,
      db_property: "sms_opt_in",
      allow_multiple_responses: false,
      editable: false,
      effort_id: effortId,
      email_data_template_ref: null,
      label: `Opt in to receive text messages about promotions and updates related to ${entityName} and ${effortName}. Message and data rates may apply.`,
      reportable: false,
      required: false,
      select_items: [],
      type: "checkbox",
    };

    axios
      .post(`/engagement-items/`, req)
      .then(({ data }) => {
        onChange([
          ...fields,
          {
            allow_multiple_responses: false,
            api_field_name: "--",
            default_checked: false,
            dynamic_label_field: req.dynamic_label_field,
            email_data_template_ref: "",
            engagement_item_id: data.response[0].engagement_item_id,
            label_url: "",
            label: req.label,
            multiple: false,
            name: data.response[0].engagement_item_id,
            options: [],
            reportable: false,
            required: false,
            type: "checkbox",
            db_property: "sms_opt_in",
            uuid: getUuid(),
          },
        ]);
        setLibraryVisible(false);
      })
      .catch((err) => {
        toast.error(err);
      });
  }

  function onAddressClick(arr, country) {
    let newFields = [...fields, ...arr.map((m) => ({ ...m, editable: false }))];
    const zipField = newFields.find((f) => f.db_property === "zip");
    if (zipField) {
      if (country === "ca") {
        zipField.label = "Postal Code";
      } else if (country === "both") {
        zipField.label = "Zip/Postal Code";
      } else {
        zipField.label = "Zip";
      }
    }
    onChange(newFields);
    setLibraryVisible(false);
  }

  return (
    <StyledFieldMgmt>
      <Modal
        opened={customVisible}
        onClose={() => setCustomVisble(false)}
        title={t("mixer:effort_builder:add_custom_field")}
      >
        <FieldForm
          effortId={effortId}
          hasImageField={fields.find((f) => f.type === "image") ? true : false}
          onSubmit={(e) => {
            onChange([...fields, { ...e, uuid: getUuid() }]);
            setCustomVisble(false);
          }}
          languages={languages}
        />
      </Modal>
      <Modal
        opened={libraryVisible}
        onClose={() => setLibraryVisible(false)}
        title={t("mixer:effort_builder:add_field_from_library")}
      >
        <FieldLibrary
          onClick={(e) => onFieldLibraryAdd(e)}
          onAddressSelect={onAddressClick}
        />
      </Modal>
      <Group>
        <Title order={3}>{t("common:field_plural")}</Title>
        <Button
          leftSection={<IconPlus size={12} />}
          size="xs"
          variant="light"
          radius="lg"
          onClick={() => setCustomVisble(true)}
        >
          {t("mixer:effort_builder:custom_field")}
        </Button>
        <Button
          leftSection={<IconPlus size={12} />}
          radius="lg"
          size="xs"
          variant="light"
          onClick={() => setLibraryVisible(true)}
        >
          {t("common:library")}
        </Button>
        <EffortSavedForms
          effortId={effortId}
          hasCustomQuestion={fields.find((f) => !f.db_property) ? true : false}
        />
      </Group>
      <Card mt="sm">
        {fields.map((f) => (
          <FieldItem
            key={f.uuid}
            fieldFormState={f}
            eiWithTriggerId={eiWithTriggerId}
            effortId={effortId}
            onUpdate={(e) => onFieldUpdate(e)}
            onRemoveSuccess={(e) => onFieldRemove(e)}
            postmarkTemplateNames={postmarkTemplateNames}
            languages={languages}
          />
        ))}
      </Card>
    </StyledFieldMgmt>
  );
}

const StyledFieldMgmt = styled.div`
  .meta {
    display: flex;
    margin-bottom: 0.5em;
  }
`;

export const StyledTextButton = styled.button`
  font-family: inherit;
  background: none;
  padding: 0;
  border: none;
  cursor: pointer;
  font-weight: bold;
  color: #8f8a8a;
  outline: none;
`;
